import React, { useState } from 'react';
import { 
  DashboardConfiguration, 
  DashboardOptions, 
  DashboardTab 
} from '../../services/dashboardConfigurationService';
import './ConfigurationSidebar.css';

interface ConfigurationSidebarProps {
  dashboard: DashboardConfiguration;
  options: DashboardOptions;
  availableDashboards?: DashboardConfiguration[];
  onDashboardUpdate: (updates: Partial<DashboardConfiguration>) => void;
  onDashboardLoad?: (dashboardId: number) => void;
  onDashboardDelete?: (dashboardId: number) => void;
  onAddTab: () => void;
  onUpdateTab: (tabIndex: number, updates: Partial<DashboardTab>) => void;
  onDeleteTab: (tabIndex: number) => void;
  onAddWidget: () => void;
  activeTabIndex: number;
  onTabChange: (index: number) => void;
}

type SidebarSection = 'selector' | 'dashboard' | 'tabs' | 'widgets' | 'settings';

const ConfigurationSidebar: React.FC<ConfigurationSidebarProps> = ({
  dashboard,
  options,
  availableDashboards,
  onDashboardUpdate,
  onDashboardLoad,
  onDashboardDelete,
  onAddTab,
  onUpdateTab,
  onDeleteTab,
  onAddWidget,
  activeTabIndex,
  onTabChange
}) => {
  const [activeSection, setActiveSection] = useState<SidebarSection>('selector');
  const [editingTabIndex, setEditingTabIndex] = useState<number | null>(null);
  const [editingTabName, setEditingTabName] = useState<string>('');

  const handleDashboardNameChange = (name: string) => {
    onDashboardUpdate({ name });
  };

  const handleDashboardDescriptionChange = (description: string) => {
    onDashboardUpdate({ description });
  };

  const startEditingTab = (tabIndex: number, currentName: string) => {
    setEditingTabIndex(tabIndex);
    setEditingTabName(currentName);
  };

  const saveTabName = (tabIndex: number) => {
    onUpdateTab(tabIndex, { tabName: editingTabName });
    setEditingTabIndex(null);
    setEditingTabName('');
  };

  const cancelEditingTab = () => {
    setEditingTabIndex(null);
    setEditingTabName('');
  };

  const handleTabIconChange = (tabIndex: number, tabIcon: string) => {
    onUpdateTab(tabIndex, { tabIcon });
  };

  const handleDeleteDashboard = (dashboardId: number, dashboardName: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering the load dashboard click
    
    if (window.confirm(`Are you sure you want to delete "${dashboardName}"?\n\nThis action cannot be undone.`)) {
      onDashboardDelete && onDashboardDelete(dashboardId);
    }
  };

  const renderSelectorSection = () => (
    <div className="sidebar-section">
      <h3>📂 Load Dashboard</h3>
      
      {availableDashboards && availableDashboards.length > 0 ? (
        <div className="dashboard-list">
          {availableDashboards.map((dash) => (
            <div 
              key={dash.id} 
              className={`dashboard-item ${dash.id === dashboard.id ? 'current' : ''}`}
              onClick={() => onDashboardLoad && dash.id && onDashboardLoad(dash.id)}
            >
              <div className="dashboard-info">
                <div className="dashboard-name">{dash.name}</div>
                <div className="dashboard-description">{dash.description || 'No description'}</div>
                <div className="dashboard-meta">
                  {dash.tabs?.length || 0} tabs • {dash.tabs?.reduce((sum, tab) => sum + (tab.widgets?.length || 0), 0) || 0} widgets
                </div>
              </div>
              <div className="dashboard-actions">
                {dash.id === dashboard.id && (
                  <div className="current-indicator">✓</div>
                )}
                <button 
                  className="delete-dashboard-btn"
                  onClick={(e) => handleDeleteDashboard(dash.id!, dash.name, e)}
                  title={`Delete "${dash.name}"`}
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="no-dashboards">
          <p>No saved dashboards found.</p>
          <p>Create your first dashboard using the settings below.</p>
        </div>
      )}
      
      <button 
        className="create-new-button"
        onClick={() => window.location.href = '/dashboard-config'}
        title="Create New Dashboard"
      >
        ➕ Create New Dashboard
      </button>
    </div>
  );

  const availableIcons = [
    'dashboard', 'chart-line', 'chart-bar', 'chart-pie', 'analytics', 
    'trending-up', 'activity', 'bar-chart', 'pie-chart', 'target'
  ];

  const renderDashboardSection = () => (
    <div className="sidebar-section">
      <h3>📊 Dashboard Settings</h3>
      
      <div className="form-group">
        <label htmlFor="dashboard-name">Dashboard Name</label>
        <input
          id="dashboard-name"
          type="text"
          value={dashboard.name}
          onChange={(e) => handleDashboardNameChange(e.target.value)}
          placeholder="Enter dashboard name"
          className="form-input"
        />
      </div>

      <div className="form-group">
        <label htmlFor="dashboard-description">Description</label>
        <textarea
          id="dashboard-description"
          value={dashboard.description || ''}
          onChange={(e) => handleDashboardDescriptionChange(e.target.value)}
          placeholder="Enter dashboard description"
          className="form-textarea"
          rows={3}
        />
      </div>

      <div className="form-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={dashboard.isDefault}
            onChange={(e) => onDashboardUpdate({ isDefault: e.target.checked })}
          />
          Set as default dashboard
        </label>
      </div>

      <div className="form-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={dashboard.isShared}
            onChange={(e) => onDashboardUpdate({ isShared: e.target.checked })}
          />
          Share with organization
        </label>
      </div>

      <div className="dashboard-stats">
        <div className="stat-item">
          <span className="stat-label">Tabs:</span>
          <span className="stat-value">{dashboard.tabs?.length || 0}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Widgets:</span>
          <span className="stat-value">
            {dashboard.tabs?.reduce((total, tab) => total + (tab.widgets?.length || 0), 0) || 0}
          </span>
        </div>
      </div>
    </div>
  );

  const renderTabsSection = () => (
    <div className="sidebar-section">
      <div className="section-header">
        <h3>📋 Dashboard Tabs</h3>
        <button onClick={onAddTab} className="add-button" title="Add Tab">
          + Add Tab
        </button>
      </div>

      <div className="tabs-list">
        {dashboard.tabs?.map((tab, index) => (
          <div 
            key={index} 
            className={`tab-item ${index === activeTabIndex ? 'active' : ''}`}
            onClick={() => onTabChange(index)}
          >
            <div className="tab-icon">
              <select
                value={tab.tabIcon || 'dashboard'}
                onChange={(e) => handleTabIconChange(index, e.target.value)}
                className="icon-select"
                onClick={(e) => e.stopPropagation()}
              >
                {availableIcons.map(icon => (
                  <option key={icon} value={icon}>{icon}</option>
                ))}
              </select>
            </div>

            <div className="tab-content">
              {editingTabIndex === index ? (
                <input
                  type="text"
                  value={editingTabName}
                  onChange={(e) => setEditingTabName(e.target.value)}
                  onBlur={() => saveTabName(index)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      saveTabName(index);
                    }
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      cancelEditingTab();
                    }
                  }}
                  className="tab-name-input"
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <div className="tab-info">
                  <span className="tab-name">{tab.tabName}</span>
                  <span className="tab-widgets-count">
                    {tab.widgets?.length || 0} widgets
                  </span>
                </div>
              )}
            </div>

            <div className="tab-actions">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  startEditingTab(index, tab.tabName);
                }}
                className="edit-button"
                title="Edit Tab Name"
              >
                ✏️
              </button>
              {dashboard.tabs && dashboard.tabs.length > 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteTab(index);
                  }}
                  className="delete-button"
                  title="Delete Tab"
                >
                  🗑️
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderWidgetsSection = () => {
    const currentTab = dashboard.tabs?.[activeTabIndex];
    
    return (
      <div className="sidebar-section">
        <div className="section-header">
          <h3>🧩 Widgets</h3>
          <button onClick={onAddWidget} className="add-button" title="Add Widget">
            + Add Widget
          </button>
        </div>

        <div className="current-tab-info">
          <span>Current Tab: <strong>{currentTab?.tabName || 'No Tab Selected'}</strong></span>
        </div>

        <div className="widget-types">
          <h4>Available Widget Types</h4>
          <div className="widget-type-grid">
            <div className="widget-type-card" onClick={onAddWidget}>
              <div className="widget-icon">📊</div>
              <div className="widget-type-info">
                <h5>Chart</h5>
                <p>Bar, line, pie charts</p>
              </div>
            </div>
            
            <div className="widget-type-card" onClick={onAddWidget}>
              <div className="widget-icon">📈</div>
              <div className="widget-type-info">
                <h5>Metric Card</h5>
                <p>Key performance indicators</p>
              </div>
            </div>
            
            <div className="widget-type-card" onClick={onAddWidget}>
              <div className="widget-icon">📋</div>
              <div className="widget-type-info">
                <h5>Table</h5>
                <p>Data tables and lists</p>
              </div>
            </div>
            
            <div className="widget-type-card" onClick={onAddWidget}>
              <div className="widget-icon">💡</div>
              <div className="widget-type-info">
                <h5>Insight Card</h5>
                <p>Key insights and trends</p>
              </div>
            </div>
          </div>
        </div>

        {currentTab?.widgets && currentTab.widgets.length > 0 && (
          <div className="current-widgets">
            <h4>Current Widgets</h4>
            <div className="widgets-list">
              {currentTab.widgets.map((widget, index) => (
                <div key={widget.id || index} className="widget-item">
                  <div className="widget-info">
                    <span className="widget-name">{widget.widgetName}</span>
                    <span className="widget-type">{widget.widgetType}</span>
                  </div>
                  <div className="widget-position">
                    {widget.width}×{widget.height}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSettingsSection = () => (
    <div className="sidebar-section">
      <h3>⚙️ Settings</h3>
      
      <div className="settings-group">
        <h4>Available Data</h4>
        <div className="data-summary">
          <div className="data-item">
            <span className="data-label">KPIs Available:</span>
            <span className="data-value">{options.availableKpis.length}</span>
          </div>
          <div className="data-item">
            <span className="data-label">Calculations:</span>
            <span className="data-value">{options.availableCalculations.length}</span>
          </div>
          <div className="data-item">
            <span className="data-label">Color Presets:</span>
            <span className="data-value">{options.colorPresets.length}</span>
          </div>
          <div className="data-item">
            <span className="data-label">Locations:</span>
            <span className="data-value">{options.accessibleLocations.length}</span>
          </div>
        </div>
      </div>

      <div className="settings-group">
        <h4>Grid Settings</h4>
        <p className="settings-description">
          Widgets use a 12-column grid system. Each widget can span 1-12 columns in width and 1-12 rows in height.
        </p>
      </div>

      <div className="settings-group">
        <h4>Color Presets</h4>
        <div className="color-presets">
          {options.colorPresets.slice(0, 3).map(preset => (
            <div key={preset.id} className="color-preset">
              <div className="color-preview">
                {preset.colors.slice(0, 4).map((color, index) => (
                  <div 
                    key={index} 
                    className="color-swatch" 
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
              <span className="preset-name">{preset.name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="configuration-sidebar">
      {/* Sidebar Navigation */}
      <div className="sidebar-nav">
        <button
          className={`nav-button ${activeSection === 'selector' ? 'active' : ''}`}
          onClick={() => setActiveSection('selector')}
          title="Load Dashboard"
        >
          📂
        </button>
        <button
          className={`nav-button ${activeSection === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveSection('dashboard')}
          title="Dashboard Settings"
        >
          📊
        </button>
        <button
          className={`nav-button ${activeSection === 'tabs' ? 'active' : ''}`}
          onClick={() => setActiveSection('tabs')}
          title="Manage Tabs"
        >
          📋
        </button>
        <button
          className={`nav-button ${activeSection === 'widgets' ? 'active' : ''}`}
          onClick={() => setActiveSection('widgets')}
          title="Add Widgets"
        >
          🧩
        </button>
        <button
          className={`nav-button ${activeSection === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveSection('settings')}
          title="Settings"
        >
          ⚙️
        </button>
      </div>

      {/* Sidebar Content */}
      <div className="sidebar-content">
        {activeSection === 'selector' && renderSelectorSection()}
        {activeSection === 'dashboard' && renderDashboardSection()}
        {activeSection === 'tabs' && renderTabsSection()}
        {activeSection === 'widgets' && renderWidgetsSection()}
        {activeSection === 'settings' && renderSettingsSection()}
      </div>
    </div>
  );
};

export default ConfigurationSidebar;
